import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, LogIn, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import NotificationBell from "@/components/layout/NotificationBell";

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const links = [
    { to: "/spas", label: "Browse Spas" },
    { to: "/spas-by-category/Luxury Spa", label: "Luxury" },
    { to: "/spas-by-category/Ayurvedic Spa", label: "Ayurveda" },
    { to: "/spas-by-category/Beauty Spa", label: "Beauty" },
    { to: "/blog", label: "Blog" },
    { to: "/plans", label: "Pricing" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact Us" },
  ];

  return (
    <header data-testid="site-header" className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-[#E7DDCF]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-2" data-testid="site-logo-link">
          <picture>
            <source srcSet="/logo-header.webp" type="image/webp" />
            <img
              src="/logo-header.png"
              alt="Pune Spa Guide — Relax • Refresh • Renew"
              className="h-14 md:h-16 w-auto object-contain"
              width="734"
              height="256"
              loading="eager"
            />
          </picture>
          <span className="sr-only">Pune Spa Guide</span>
        </Link>

        <nav className="hidden md:flex items-center gap-6">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="text-sm text-[#1F2937] hover:text-[#1F4E3D] transition-colors"
              data-testid={`header-nav-${l.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              <NotificationBell enabled={user.role === "owner"} />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => nav(user.role === "admin" ? "/admin" : "/dashboard")}
                data-testid="header-dashboard-button"
                className="gap-2"
              >
                <LayoutDashboard size={16} /> Dashboard
              </Button>
              <Button variant="outline" size="sm" onClick={() => { logout(); nav("/"); }} data-testid="header-logout-button">
                Logout
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-link">
                <Button variant="ghost" size="sm" className="gap-2">
                  <LogIn size={16} /> Login
                </Button>
              </Link>
              <Link to="/register" data-testid="header-list-your-spa-button">
                <Button size="sm" className="bg-[#C9A86A] hover:bg-[#D7BC86] text-[#111827] gap-2">
                  List Your Spa
                </Button>
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="header-mobile-menu-button"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#E7DDCF] bg-white">
          <div className="px-4 py-3 flex flex-col gap-2">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="py-2 text-sm">
                {l.label}
              </Link>
            ))}
            <div className="pt-2 border-t border-[#E7DDCF] flex gap-2">
              {user ? (
                <>
                  <Button variant="ghost" className="flex-1" onClick={() => { setOpen(false); nav(user.role === "admin" ? "/admin" : "/dashboard"); }}>
                    Dashboard
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => { logout(); setOpen(false); nav("/"); }}>Logout</Button>
                </>
              ) : (
                <>
                  <Link to="/login" className="flex-1" onClick={() => setOpen(false)}>
                    <Button variant="outline" className="w-full">Login</Button>
                  </Link>
                  <Link to="/register" className="flex-1" onClick={() => setOpen(false)}>
                    <Button className="w-full bg-[#C9A86A] hover:bg-[#D7BC86] text-[#111827]">List Your Spa</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
